/**
 * Content registry — every editable string on the storefront lives here.
 * The Admin Design tab renders this registry as an editable form.
 *
 * Structure:
 *   - PAGES  → high-level buckets shown as the top-level page filter
 *   - Each section has a `page` field mapping it to one of the pages above.
 *   - Each item has a stable slug (`key`), a `default` value, and an optional hint.
 *
 * Convention for italic accent styling: fields ending in ".title_accent" render
 * italic in the flamingo/brown accent color. The main title field is normal weight.
 */

export const PAGES = [
  { id: 'global', label: 'Global (Announcement, Footer)' },
  { id: 'home', label: 'Home page' },
  { id: 'shop', label: 'Shop / Collections' },
  { id: 'product', label: 'Product detail' },
  { id: 'about', label: 'About page' },
  { id: 'blog', label: 'Journal / Blog' },
  { id: 'contact', label: 'Contact page' },
];

export const CONTENT_SECTIONS = [
  // ================= GLOBAL =================
  {
    id: 'announcement', page: 'global',
    label: 'Announcement bar (top scrolling strip)',
    items: [
      { key: 'announcement.item.1', default: 'FREE SHIPPING ON ORDERS OVER $500 (US)', hint: 'Rotating strip message 1 — leave blank to remove' },
      { key: 'announcement.item.2', default: 'PERSONALIZE ANY PRODUCT — LASER ENGRAVING AVAILABLE' },
      { key: 'announcement.item.3', default: 'NEW FAITH CAP COLLECTION IS LIVE — SHOP NOW' },
      { key: 'announcement.item.4', default: 'NOW SHIPPING ACROSS THE US & CANADA' },
    ],
  },
  {
    id: 'footer.newsletter', page: 'global',
    label: 'Footer — Newsletter block',
    items: [
      { key: 'footer.newsletter.title', default: 'Stay in touch', hint: 'Main headline' },
      { key: 'footer.newsletter.title_accent', default: '', hint: 'Italic accent word (leave blank for none)' },
      { key: 'footer.newsletter.subtitle', default: 'News, new drops and stories of purpose — straight to your inbox.' },
      { key: 'footer.newsletter.placeholder', default: 'Enter your email' },
      { key: 'footer.newsletter.cta', default: 'Subscribe' },
    ],
  },
  {
    id: 'footer.review', page: 'global',
    label: 'Footer — "Share your story" QR block',
    items: [
      { key: 'footer.review.eyebrow', default: 'Share your story' },
      { key: 'footer.review.title', default: 'Loved your Wear The Hope with Ben piece?' },
      { key: 'footer.review.title_accent', default: 'Tell the world.', hint: 'Italic accent phrase' },
      { key: 'footer.review.body', default: 'Scan the QR — or use the link below — to leave a review. Your words help other believers find us.' },
      { key: 'footer.review.cta', default: 'Leave a review' },
      { key: 'footer.review.instagram', default: 'Tag @wearthehope' },
    ],
  },
  {
    id: 'footer.brand', page: 'global',
    label: 'Footer — Brand column',
    items: [
      { key: 'footer.tagline', default: 'A motivational gift brand — declaring love and hope through everyday products. Handcrafted with intention since 2018. Now shipping across the US & Canada.' },
    ],
  },
  {
    id: 'footer.columns', page: 'global',
    label: 'Footer — Column headings & links',
    items: [
      { key: 'footer.col.shop', default: 'Shop' },
      { key: 'footer.col.company', default: 'Company' },
      { key: 'footer.col.contact', default: 'Contact' },
      { key: 'footer.links.story', default: 'Our Story' },
      { key: 'footer.links.contact', default: 'Contact Us' },
      { key: 'footer.links.size', default: 'Size Guide' },
      { key: 'footer.links.terms', default: 'Terms & Conditions' },
      { key: 'footer.links.privacy', default: 'Privacy Policy' },
      { key: 'footer.links.refund', default: 'Refund & Cancellation' },
      { key: 'footer.us_partner.cta', default: 'Visit US Partner' },
    ],
  },
  {
    id: 'footer.contact', page: 'global',
    label: 'Footer — Contact details',
    items: [
      { key: 'footer.contact.phone', default: '+1 (555) 123-4567', hint: 'Phone number shown in the footer contact column' },
      { key: 'footer.contact.email', default: 'hello@wearthehope.com', hint: 'Support email shown in the footer contact column' },
      { key: 'footer.contact.location', default: 'New York, NY — Ships across the US & Canada' },
    ],
  },
  {
    id: 'footer.legal', page: 'global',
    label: 'Footer — Legal strip (bottom)',
    items: [
      { key: 'footer.legal.copyright', default: '© {year} Wear The Hope with Ben. All rights reserved.', hint: '{year} is auto-replaced with the current year' },
      { key: 'footer.legal.made', default: 'Handcrafted with love — Ships across the US & Canada' },
    ],
  },

  // ================= HOME =================
  {
    id: 'home.hero', page: 'home',
    label: 'Home — Hero (primary slide)',
    items: [
      { key: 'home.hero.eyebrow', default: 'Purpose with Hope', hint: 'Small badge above the headline' },
      { key: 'home.hero.title', default: 'Our story can be summed up in two words…', hint: 'Main headline (first line)' },
      { key: 'home.hero.accent', default: 'But God.', hint: 'Italic accent line (gold, second line)' },
      { key: 'home.hero.subtitle', default: 'Handcrafted apparel, tumblers and keepsakes made by adults with special needs — every product carries a story of redemption.', hint: 'Paragraph below the headline' },
      { key: 'home.hero.cta.primary', default: 'Shop with Purpose', hint: 'Primary CTA button' },
      { key: 'home.hero.cta.secondary', default: 'Read Our Story', hint: 'Secondary text link' },
      { key: 'home.hero.stat1.value', default: 'L3C', hint: 'Big number on badge 1' },
      { key: 'home.hero.stat1.label', default: 'Social Enterprise', hint: 'Label on badge 1' },
      { key: 'home.hero.stat2.value', default: 'Every' },
      { key: 'home.hero.stat2.label', default: 'Product Carries Hope' },
      { key: 'home.hero.stat3.value', default: 'For' },
      { key: 'home.hero.stat3.label', default: 'Adults with Special Needs' },
    ],
  },
  {
    id: 'home.hero.images', page: 'home',
    label: 'Home — Hero Slide Images',
    items: [
      { key: 'home.hero.slide1.image', type: 'image', default: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx0c2hpcnR8ZW58MHx8fHwxNzgzMzI2MTkwfDA&ixlib=rb-4.1.0&q=85', hint: 'Primary hero slide image (Slide 1)' },
      { key: 'home.hero.slide2.image', type: 'image', default: 'https://images.unsplash.com/photo-1627123424574-724758594e93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHx3YWxsZXR8ZW58MHx8fHwxNzgzMzI2MTkwfDA&ixlib=rb-4.1.0&q=85', hint: 'Second slide image' },
      { key: 'home.hero.slide3.image', type: 'image', default: 'https://images.unsplash.com/photo-1588850561407-ed78c282e89b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxjYXB8ZW58MHx8fHwxNzgzMzI2MTE3fDA&ixlib=rb-4.1.0&q=85', hint: 'Third slide image' },
    ],
  },
  {
    id: 'home.strip', page: 'home',
    label: 'Home — Feature strip (4 icon items)',
    items: [
      { key: 'home.strip.1.title', default: 'Ships to US & Canada' },
      { key: 'home.strip.1.sub',   default: 'Free over $500 in the US' },
      { key: 'home.strip.2.title', default: 'Fully Customizable' },
      { key: 'home.strip.2.sub',   default: 'Laser engraving on request' },
      { key: 'home.strip.3.title', default: 'Handcrafted Quality' },
      { key: 'home.strip.3.sub',   default: 'Every item checked twice' },
      { key: 'home.strip.4.title', default: 'Easy Returns' },
      { key: 'home.strip.4.sub',   default: '7-day hassle-free returns' },
    ],
  },
  {
    id: 'home.categories', page: 'home',
    label: 'Home — "Shop by Category" block',
    items: [
      { key: 'home.categories.eyebrow', default: 'Curated Categories' },
      { key: 'home.categories.title', default: 'Shop by', hint: 'Main heading (normal weight)' },
      { key: 'home.categories.title_accent', default: 'purpose', hint: 'Italic accent word' },
      { key: 'home.categories.subtitle', default: '', hint: 'Optional lead paragraph — leave blank to hide' },
    ],
  },
  {
    id: 'home.about', page: 'home',
    label: 'Home — About story block',
    items: [
      { key: 'home.about.eyebrow', default: 'Our Story' },
      { key: 'home.about.title', default: 'Two words changed everything —', hint: 'Main heading' },
      { key: 'home.about.title_accent', default: 'But God.', hint: 'Italic accent phrase' },
      { key: 'home.about.body', default: 'Twenty-four years ago, God blessed us with our first child, Ben. When he was two and a half, we learned he was on the autism spectrum. The years that followed were filled with questions — until God connected us with Paul Isac in November 2023 and gave birth to Wear The Hope with Ben.\n\nToday we are an L3C social enterprise creating meaningful employment for adults with special needs. Every product tells a story. Every product carries hope. Every purchase creates opportunity.', hint: 'Multi-paragraph body — new lines preserved' },
      { key: 'home.about.cta', default: 'Read Full Story', hint: 'Text link below the CTA' },
      { key: 'home.about.catalogue_cta', default: 'Download Catalogue', hint: 'Solid button label' },
      { key: 'home.about.badge_year', default: 'Since 2023', hint: 'White card badge — big line' },
      { key: 'home.about.badge_sub',  default: 'Purpose stitched into every product', hint: 'White card badge — small line' },
    ],
  },
  {
    id: 'home.instagram', page: 'home',
    label: 'Home — Instagram feed block',
    items: [
      { key: 'home.instagram.eyebrow', default: 'Follow along' },
      { key: 'home.instagram.title_main', default: '@', hint: 'Prefix before the italic handle (normal weight)' },
      { key: 'home.instagram.title_accent', default: 'wearthehope', hint: 'Italic accent (Instagram handle)' },
      { key: 'home.instagram.body', default: 'Tag us in your Wear The Hope with Ben moments and get featured — over 10K+ believers spreading love and hope.' },
      { key: 'home.instagram.url', default: 'https://www.instagram.com/wearthehope', hint: 'Instagram profile URL' },
    ],
  },
  {
    id: 'home.testimonials', page: 'home',
    label: 'Home — Testimonials block',
    items: [
      { key: 'home.testimonials.eyebrow', default: 'What Our Customers Say' },
      { key: 'home.testimonials.title', default: 'Loved by', hint: 'Main heading' },
      { key: 'home.testimonials.title_accent', default: 'believers worldwide', hint: 'Italic accent phrase' },
      { key: 'home.testimonials.rating_label', default: '4.9 / 5 on Google Reviews' },
    ],
  },

  // ================= SHOP =================
  {
    id: 'shop', page: 'shop',
    label: 'Shop / Collections page',
    items: [
      { key: 'shop.title', default: 'All Collections', hint: 'Page heading' },
      { key: 'shop.subtitle', default: 'Explore our full range', hint: 'Subtitle beneath the heading' },
      { key: 'shop.filters.title', default: 'Filters' },
      { key: 'shop.empty.title', default: 'Nothing here yet' },
      { key: 'shop.empty.body', default: 'Try adjusting your filters or explore other collections.' },
    ],
  },

  // ================= ABOUT =================
  {
    id: 'about', page: 'about',
    label: 'About page — Hero + Mission + Values',
    items: [
      { key: 'about.hero.eyebrow', default: 'Our Story' },
      { key: 'about.hero.title', default: 'A small hobby that became', hint: 'Main heading' },
      { key: 'about.hero.title_accent', default: 'a movement of hope', hint: 'Italic accent phrase (second line)' },
      { key: 'about.hero.body', default: 'In December 2018, we offered our "5 loaves and 2 fish" to God with a simple prayer — to impact the people of nations through motivational, faith-inspired gifts.' },
      { key: 'about.mission.eyebrow', default: 'Our Mission' },
      { key: 'about.mission.title', default: 'To share the message of', hint: 'Main heading' },
      { key: 'about.mission.title_accent', default: 'love & hope — one product at a time', hint: 'Italic accent phrase' },
      { key: 'about.mission.body', default: 'We create and customize products for individuals, teams, organizations and churches. From wallets to wall frames, mugs to caps — each item is thoughtfully designed to remind you of who you are in Christ.' },
      { key: 'about.mission.quote', default: '"We sincerely hope our products will impact your life and those around you."', hint: 'Italic quote in flamingo color' },
      { key: 'about.values.eyebrow', default: 'What we stand for' },
      { key: 'about.values.title', default: 'Values that shape everything we do' },
    ],
  },

  // ================= BLOG =================
  {
    id: 'blog', page: 'blog',
    label: 'Journal (Blog) — Index page',
    items: [
      { key: 'blog.eyebrow', default: 'The journal' },
      { key: 'blog.title', default: 'Stories of', hint: 'Main heading' },
      { key: 'blog.title_accent', default: 'hope', hint: 'Italic accent word' },
      { key: 'blog.subtitle', default: 'Craft notes, gifting guides and stories behind the pieces we make.' },
    ],
  },

  // ================= CONTACT =================
  {
    id: 'contact', page: 'contact',
    label: 'Contact page — Hero, Locations, Form',
    items: [
      { key: 'contact.eyebrow', default: "We'd love to hear from you" },
      { key: 'contact.title', default: 'Get in', hint: 'Main heading' },
      { key: 'contact.title_accent', default: 'touch', hint: 'Italic accent word' },
      { key: 'contact.subtitle', default: 'Custom order, bulk enquiry, partnership or just to say hi — our inbox is always open.' },
      { key: 'contact.locations.title', default: 'Our', hint: 'Locations block heading' },
      { key: 'contact.locations.title_accent', default: 'locations', hint: 'Italic accent word' },
      { key: 'contact.form.name', default: 'Your name' },
      { key: 'contact.form.email', default: 'Email' },
      { key: 'contact.form.message', default: 'Message' },
      { key: 'contact.form.submit', default: 'Send Message' },
    ],
  },

  // ================= PRODUCT DETAIL =================
  {
    id: 'product', page: 'product',
    label: 'Product detail — Labels & tabs',
    items: [
      { key: 'pd.details', default: 'Details' },
      { key: 'pd.shipping', default: 'Shipping' },
      { key: 'pd.reviews', default: 'Reviews' },
      { key: 'pd.customize.label', default: 'Personalize (optional)' },
      { key: 'pd.customize.placeholder', default: 'Add a name or short verse' },
      { key: 'pd.badge.free_ship.title', default: 'Free Shipping' },
      { key: 'pd.badge.free_ship.sub', default: 'On US orders $500+' },
      { key: 'pd.badge.returns.title', default: 'Easy Returns' },
      { key: 'pd.badge.returns.sub', default: '30-day window' },
      { key: 'pd.badge.gift.title', default: 'Gift Wrapped' },
      { key: 'pd.badge.gift.sub', default: 'Ready to give' },
      { key: 'pd.taxes_note', default: 'Sales tax calculated at checkout' },
    ],
  },
];

// Flatten default map for O(1) lookup at runtime
export const CONTENT_DEFAULTS = CONTENT_SECTIONS.reduce((acc, section) => {
  section.items.forEach(item => { acc[item.key] = item.default; });
  return acc;
}, {});
